import {
  Component,
  Pipe,
  LOCALE_ID,
  Inject,
  PipeTransform,
  Input,
  ContentChild,
  TemplateRef,
  ContentChildren,
  QueryList,
  OnInit,
  ChangeDetectorRef,
  forwardRef,
  OnDestroy,
  AfterContentInit,
  OnChanges,
  SimpleChanges,
  Type,
  Injector,
  ElementRef, Directive, ViewChildren, AfterViewInit
} from "@angular/core";
import { RestClient } from "../Api/DcoApi/Client/RestClient";
import { ActivatedRoute, Router } from "@angular/router";
import { CommonModule, Location } from '@angular/common';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import {Observable, OperatorFunction, Subject, Subscription, takeUntil} from "rxjs";
import { DateSelect } from "../Akte/Controlls/DateSelect";
import { DatePipe, DecimalPipe } from '@angular/common';
import { StatusPipe } from "../Pipes/StatusPipe";
import {AbsetzungsStatusPipe} from '../Pipes/AbsetzungsStatusPipe';
import {NgbTypeahead} from '@ng-bootstrap/ng-bootstrap';
import {Kooperationspartner} from '../Api/DcoApi/Kooperationspartner';
import {debounceTime, map} from 'rxjs/operators';


@Pipe({
  name: 'universalFormat',
  standalone: true // Ab Angular 14+ empfohlen
})
export class UniversalFormatPipe implements PipeTransform {

  // Wir holen uns die Angular-Standard-Pipes über Dependency Injection
  private datePipe: DatePipe;
  private decimalPipe: DecimalPipe;

  constructor(@Inject(LOCALE_ID) private locale: string, private injector: Injector) {
    this.datePipe = new DatePipe(this.locale);
    this.decimalPipe = new DecimalPipe(this.locale);
  }

  transform(value: any, format: string, type: Types, pipeClass?: Type<any>): string {
    if (value === null || value === undefined || value === '') return '';

    if (pipeClass) {
      try {
        // Holt die Instanz der übergebenen Pipe aus dem Angular Dependency Injection System
        const pipeInstance = this.injector.get(pipeClass);

        if(format && format !== null && format.trim() !== '')
          return pipeInstance.transform(value, format);
        // Argumente entpacken und transform aufrufen
        return pipeInstance.transform(value);
      } catch (e) {
        console.log(`Pipe konnte nicht aufgelöst werden. Ist sie im providers-Array registriert?`, e);
      }

    }

    if (type == Types.Date && !(value instanceof Date))
      value = new Date(value);

    // 1. Fall: Es ist ein echtes Date-Objekt oder ein ISO-Datum-String
    if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)) && isNaN(Number(value)))) {
      return this.datePipe.transform(value, format || 'mediumDate') || '';
    }

    // 2. Fall: Es ist eine Zahl (Integer oder Float)
    if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '')) {
      // Wenn kein Format angegeben ist, geben wir die Zahl normal aus
      return format ? (this.decimalPipe.transform(value, format) || '') : value.toString();
    }

    // 3. Fall: Es ist ein normaler String
    if (typeof value === 'string') {
      if (format === 'uppercase') return value.toUpperCase();
      if (format === 'lowercase') return value.toLowerCase();
      return value;
    }

    // Fallback für Objekte etc.
    return String(value);
  }
}


@Component({
  selector: 'app-dynamic-control',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule,DateSelect],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DynamicControlComponent),
      multi: true
    }
  ],
  // 2. Inline-HTML-Template mit ngSwitch
  template: `
    <div class="dynamic-control-wrapper">
      <label *ngIf="label" class="control-label">{{ label }}</label>

      <div [ngSwitch]="type">

        <input *ngSwitchCase="ControlTypes.String"
               type="text"
               [formControl]="internalControl"
               class="form-input" />

        <div *ngSwitchCase="ControlTypes.Bool" class="checkbox-container">
          <label class="checkbox-label">
            <input type="checkbox" [formControl]="internalControl" />
            <span class="checkbox-text">Auswählen</span>
          </label>
        </div>

        <input *ngSwitchCase="ControlTypes.Number"
               type="number"
               [formControl]="internalControl"
               class="form-input" />

       <div *ngSwitchCase="ControlTypes.Date">
          <Date-Select
            [Date]="internalControl.value"
            (DateChange)="onCustomDateChange($event)">
          </Date-Select>
        </div>

        <input *ngSwitchDefault
               type="text"
               [formControl]="internalControl"
               class="form-input" />
      </div>
    </div>
  `,
  // 3. Optionales Inline-Styling für ein sauberes Standard-Aussehen
  styles: [`
    .dynamic-control-wrapper {
      margin-bottom: 1rem;
      font-family: sans-serif;
    }
    .control-label {
      display: block;
      margin-bottom: 0.35rem;
      font-weight: 600;
      font-size: 0.9rem;
      color: #333;
    }
    .form-input {
      width: 100%;

      padding: 0.5rem;
      font-size: 1rem;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-sizing: border-box;
    }
    .form-input:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
    }
    .checkbox-container {
      padding: 0.25rem 0;
    }
    .checkbox-label {
      display: inline-flex;
      align-items: center;
      cursor: pointer;
    }
    .checkbox-text {
      margin-left: 0.5rem;
      font-size: 0.95rem;
    }
  `]
})
export class DynamicControlComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() type: Types = Types.String;
  @Input() label: string = '';



  // Macht das Enum innerhalb des Inline-Templates sichtbar
  protected readonly ControlTypes = Types;

  // Das interne Form-Control steuert den eigentlichen Eingabewert
  protected internalControl = new FormControl<any>(null);

  private destroy$ = new Subject<void>();

  // Callbacks für die Anbindung an die Angular Forms API (ngModel)
  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  ngOnInit(): void {
    // Erkennt interne Änderungen (Eingabe durch den User) und streamt sie ins Toway-Binding nach außen
    this.internalControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        // HTML-Typen wie 'number' senden oft standardmäßig Strings. Das korrigieren wir hier direkt.
        if (this.type === Types.Number && value !== null && value !== '') {
          value = Number(value);
        }
        this.onChange(value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Wird gefeuert, wenn sich der Wert von AUẞEN (aus dem Parent über das Binding) ändert
  writeValue(value: any): void {
    this.internalControl.setValue(value, { emitEvent: false });
  }

  protected onCustomDateChange(newDate: any): void {
    this.internalControl.setValue(newDate);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.internalControl.disable() : this.internalControl.enable();
  }
}

export interface FilterListEntry
{
  Key: string;
  Value: string | null;
}

@Directive({
  selector: '[FilterPopoverRef]',
  standalone: true
})
export class FilterPopoverRefDirective implements  AfterViewInit {

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      const firstInput = this.el.nativeElement.querySelector('input[type="text"], input[type="number"], input[datemask]');
      if (firstInput) {
        (firstInput as HTMLElement).focus();
      }
    });
    }

}

@Component({
  selector: 'DataGridViewColumn',
  template: '', // Bleibt leer
  standalone: true
})
export class DataGridViewCollumn<T> implements OnDestroy{
  @Input() header: string = '';
  @Input() field: string = '';
  @Input() class: string = '';
  @Input() formart: string = '';
  @Input() headerClass: string = '';
  @Input() cellClass: (data: T, field: any) => string = () => '';
  @Input() isFixed: boolean = false;
  @Input() isKey: boolean = false;
  @Input() AllowOrder: boolean = true;
  @Input() AllowSearch: boolean = true;
  @Input() DataType: Types = Types.String;
  @Input() DataFilterList: FilterListEntry[] | null = null;


  private filterInput$ = new Subject<DataGridView<T>>();
  private filterSubscription!: Subscription;

  @Input() pipe?: Type<any>;
  // Optional: Für benutzerdefinierte Zellen-Templates
  @ContentChild('cell', { read: TemplateRef }) cellTemplate?: TemplateRef<any>;
  @ContentChild('header', { read: TemplateRef }) headerTemplate?: TemplateRef<any>;

  ShowFilter: boolean = false;

  ngOnDestroy(): void {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
  }

  constructor() {
    // 2. Subscribe with debounceTime operator
    this.filterSubscription = this.filterInput$.pipe(
      debounceTime(200)
    ).subscribe((grid: DataGridView<T>) => {
      grid.LoadData();

    });
  }



  public formatter: (obj: FilterListEntry) => string = obj => obj.Key;

  public CurrentFilterValue(): FilterListEntry | null
  {

    if(this.DataFilterList && this.HeaderFilter && this.HeaderFilter !== '')
    {
      return this.DataFilterList.find(o => o.Value?.toLowerCase() === this.HeaderFilter.toLowerCase()) ?? null;
    }

    return null;
  }

  public FilterItems: OperatorFunction<string, FilterListEntry[]> = (text$: Observable<string>) =>
  text$.pipe(
    debounceTime(200),
      map((term) =>
      term === '' || this.DataFilterList == null ? [] : this.DataFilterList.filter(o => o.Key.toLowerCase().includes(term.toLowerCase())).slice(0, 10),
    ),
  );

  get FileterIcon(): string {

    if (this.Filter && this.Filter != null)
      return "Filtered";

    return "";
  }

  Filter: SpecificationTransfer | null = null;
  _filterInternal: SpecificationTransfer = { s: "EQ", p: this.field } as SpecificationTransfer;

  TooggleFilter() {
    this.ShowFilter = !this.ShowFilter;
    if (this.ShowFilter)
    {
      this._filterInternal = this.Filter ?? { s: "EQ", p: this.field } as SpecificationTransfer;

    }

  }

  UseSecoundSelect(): boolean {
    if (this._filterInternal?.s == "BTW")
      return true;
    return false;
  }

  FilterSave() {
    this.Filter = this._filterInternal;
  }

  FilterReset() {
    this._filterInternal = { s: "EQ", p: this.field } as SpecificationTransfer;
    this.FilterSave();

  }
  setValues(init?: any): DataGridViewCollumn<T>{
    if (init) {
      Object.keys(init).forEach((key) => {
        const val = (init as any)[key];
        // Nur zuweisen, wenn der Wert nicht undefined ist
        if (val !== undefined) {
          (this as any)[key] = val;
        }
      });
    }
    return this;
  }

  setHeaderFilter(text: string, grid: DataGridView<T>) {

    if (text && text.trim() !== '') {
      this._filterInternal = this.Filter ?? { s: "EQ", p: this.field } as SpecificationTransfer;
      if (this.DataType != Types.Date)
        this._filterInternal.v1 = text;
      else {
        const date = text.split('.');

        this._filterInternal.v1 = JSON.stringify(new Date(Date.UTC(parseInt(date[2], 10), parseInt(date[1], 10) - 1, parseInt(date[0], 10))))
      }
      this._filterInternal.s = this.IsString ? "LK" : "EQ";
      this.FilterSave();
    }
    else
      this.FilterReset();

    this.filterInput$.next(grid);
  }

  get HeaderFilter() : string {
    return this._filterInternal.v1;
  }

  get IsString(): boolean {
    return this.DataType == Types.String;
  }

  get IsDate(): boolean {
    return this.DataType == Types.Date;
  }

  get PossibleStatements(): any[] {
    switch (this.DataType) {
      case Types.String:
        return [
          { key: "EQ", Value: "Gleich" },
          { key: "LK", Value: "Enthält" },
        ];
        break;
      case Types.Bool:
        return [
          { key: "EQ", Value: "Gleich" },
        ];
        break;
      case Types.Number:
        return [
          { key: "EQ", Value: "Gleich" },
          { key: "BTW", Value: "Zwischen" },
          { key: "GR", Value: "Größer" },
          { key: "GRE", Value: "Größer oder Gleich" },
          { key: "SM", Value: "kleiner" },
          { key: "SME", Value: "kleiner oder Gleich" },
        ];
        break;
      case Types.Date:
        return [
          { key: "EQ", Value: "Gleich" },
          { key: "BTW", Value: "Zwischen" },
          { key: "GR", Value: "Größer" },
          { key: "GRE", Value: "Größer oder Gleich" },
          { key: "SM", Value: "kleiner" },
          { key: "SME", Value: "kleiner oder Gleich" },
        ];
        break;
    }
    return [];
  }

}

@Component({
  imports: [
    CommonModule,
    FormsModule, DynamicControlComponent, UniversalFormatPipe, StatusPipe, NgbTypeahead, FilterPopoverRefDirective
  ],  selector: 'DataGridView',
  templateUrl: './DataGridView.html',
  standalone: true,
  styleUrl: './DataGridView.css',
  providers: [StatusPipe, AbsetzungsStatusPipe]
})
export class DataGridView<T> implements AfterContentInit, OnChanges {
  @Input() DataUrl: string = "";
  @Input() KeyProperty: string = "";
  @Input() UseMultiSort: boolean = false;
  @Input() UseUrlFilter: boolean = true;
  data: ListBaseModel<T> = { querry: [], pageCount: 0, anzahlGesamt: 0 };
  ApiClient: RestClient = new RestClient()
  @ContentChildren(DataGridViewCollumn) columns!: QueryList<DataGridViewCollumn<T>>;

  Selected: any = null;
  @Input() SelectedChanged = (o:any)  => {};
  @Input() CustomiseRowCssClass = (o:any)  => {return ""};

  SortDataList: SortData[] = [];

  GetSortendIcon(field: string): string {
    var entry = this.SortDataList.find(o => o.field == field);

    if (!entry || entry == null)
      return 'bi bi-arrow-down-up';

    if (entry.isDESC)
      return 'bi bi-sort-down';
    else
      return 'bi bi-sort-down-alt';
  }



  SetOrder(field: string) {
    var entry = this.SortDataList.find(o => o.field == field?.toLowerCase());

    if (!entry || entry == null) {
      if (!this.UseMultiSort) {
        this.SortDataList = [];
      }

      this.SortDataList.push({ field: field?.toLowerCase(), isDESC: false });
    }
    else {

      if (!entry.isDESC)
        entry.isDESC = true;
      else
        this.SortDataList = this.SortDataList.filter(o => o.field != field?.toLowerCase());
    }

    this.LoadData();
  }

  CurrentPage = 1;
  CurrentPageSize = 25;
  PageSizes = [
    25,50,100,1000
  ]

  IsLoading: boolean = true;
  constructor(private location: Location, private router: Router, private route: ActivatedRoute, private cdr: ChangeDetectorRef) { }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['DataUrl'] || changes['KeyProperty'] || changes['UseMultiSort']) {
      this.LoadData();
    }
  }


  ngAfterContentInit() {


    this.route.queryParams.subscribe(params => {

      if(this.UseUrlFilter) {
        if(params != undefined && params != null)
        {
          this.CurrentPage = params['page'] ?? 1;
          this.CurrentPageSize = params['pageSize'] ?? 25;

          if (params['order']) {
            var split: string[] = params['order'].split("||")

            this.SortDataList = split.map(o => {
              var s = o.split('|');
              var item = {field: s[0]?.trim()?.toLowerCase()} as SortData;
              item.isDESC = s[1]?.toLowerCase().trim() == 'desc';

              return item;
            });

          } else
            this.SortDataList = [];

          if (params['q']) {
            var items: SpecificationTransfer[] = JSON.parse(this.base64ToUtf8(params['q'])) as SpecificationTransfer[]

            items.forEach(v => {
              var col = this.columns?.find(o => o.field?.toLowerCase().trim() == v.p?.toLowerCase().trim())
              if (col && col != null)
                col.Filter = v;
            });
          }
        }
        else
        {
          this.CurrentPage = 1;
          this.CurrentPageSize = 25;
          this.SortDataList = [];
        }
      }


      this.LoadData();
    });
  }

  IsSelected(row: any) : boolean {
    if (!this.Selected || this.Selected == null || this.KeyProperty == "" || this.KeyProperty == null || !this.KeyProperty)
      return false;

    return this.getNestedValue(this.Selected, this.KeyProperty) == this.getNestedValue(row, this.KeyProperty);
  }

  SelectRow(row: any) {
    this.Selected = row;
    if(this.SelectedChanged)
      this.SelectedChanged(row);
  }

  SelectRowByID(id :any) {
    this.Selected = {};
    this.Selected[this.KeyProperty] = id;
  }



  setColFiletr(col:any, evt:any){
    col.setHeaderFilter(evt, this);

    //this.LoadData();
  }

  async LoadData() {

    if (this.CurrentPage < 1)
      this.CurrentPage = 1;
    if (this.CurrentPageSize < 0)
      this.CurrentPageSize = 25;

    this.data = await this.ApiClient.Get<ListBaseModel<T>>(`${this.DataUrl}${this.DataUrl.indexOf("?") > 0 ? "&" : "?"}page=${(this.CurrentPage)}&pageSize=${this.CurrentPageSize}&order=${this.SortDataList.map(o => `${o.field?.toLowerCase()}|${(o.isDESC ? "desc" : "asc")}`).join('||')}&q=${this.FiletrQuerry}`);
    if(this.CurrentPage > this.data.pageCount) {
      this.CurrentPage = 1;
      this.data = await this.ApiClient.Get<ListBaseModel<T>>(`${this.DataUrl}${this.DataUrl.indexOf("?") > 0 ? "&" : "?"}page=${(this.CurrentPage)}&pageSize=${this.CurrentPageSize}&order=${this.SortDataList.map(o => `${o.field?.toLowerCase()}|${(o.isDESC ? "desc" : "asc")}`).join('||')}&q=${this.FiletrQuerry}`);
    }

    this.updateUrlSilent();
    this.IsLoading = false;
    this.cdr.detectChanges();
  }

  async navigateTo(page: number) {

    if (page <= 0 || page > this.data.pageCount)
      return;

    this.CurrentPage = page;

    await this.LoadData();
  }

  get FiletrQuerry(): string {
    var items = this.columns?.map(o => o.Filter).filter(o => o != null && o != undefined);
    return this.utf8ToBase64(JSON.stringify(items))
  }

  setColOnEnter(col:any)
  {
    setTimeout(()=> { col.FilterSave(); this.LoadData(); }, 200);
  }


  utf8ToBase64(str:string ) {
    // 1. Convert string to UTF-8 bytes
    const bytes = new TextEncoder().encode(str);

    // 2. Convert bytes to a binary string
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    // 3. Base64 encode the binary string
    return btoa(binary);
  }
  updateUrlSilent() {

    if(!this.UseUrlFilter)
      return;
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: {
        page: this.CurrentPage,
        pageSize: this.CurrentPageSize,
        order: this.SortDataList.map(o => `${o.field?.toLowerCase()}|${(o.isDESC ? "desc" : "asc")}`).join('||'),
        q: this.FiletrQuerry
      },
      queryParamsHandling: 'merge'
    });
    const serializedUrl = this.router.serializeUrl(urlTree);
    this.location.go(serializedUrl);
  }

  base64ToUtf8(base64:string) {
    // 1. Base64 decode to binary string
    const binary = atob(base64);

    // 2. Convert binary string characters to a byte array
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    // 3. Decode UTF-8 bytes back to a JavaScript string
    return new TextDecoder().decode(bytes);
  }

  get Pages(): number[] {

    var items: number[] = [];

    if (this.CurrentPage > 1) {
      var i = Number(this.CurrentPage) - 1;
      do {
        items.push(i);
        i = i - 1;
      } while (i > 1 && items.length < 4)

    }

    items.push(this.CurrentPage);
    var itemsCounter = 9 - items.length;
    for (var i = 1; i <= itemsCounter; i++) {

      if (this.CurrentPage + i <= this.data.pageCount)
        items.push(Number(this.CurrentPage) + i);
    }

    return items.sort((a, b) => a - b);
  }

  get MoreThen4Left(): boolean {
    return (Number(this.CurrentPage) - 1) > 4;
  }

  get MoreThen4Right(): boolean {
    return (Number(this.data.pageCount) - Number(this.CurrentPage)) > 4;
  }

  getNestedValue(row: any, path: string): any {
    if (!row || !path) return '';

    // Reduce walks through the array of keys ['a', 'b'] starting from the row object
    return path.split('.').reduce((currentObject, key) => {
      return currentObject && currentObject[key] !== undefined ? currentObject[key] : '';
    }, row);
  }
  get SkeletonRows(): any[] {
    return new Array(Number(this.CurrentPageSize));
  }


  protected readonly Math = Math;
  protected readonly setTimeout = setTimeout;
}


interface ListBaseModel<T> {
  querry: T[];
  pageCount: number;
  anzahlGesamt: number;
}


interface SortData {
  field: string;
  isDESC: boolean;
}

interface SpecificationTransfer {
  p: string;
  s: string;
  n: boolean;
  v1: any;
  v2?: any;
}

export enum Types {
  String = 1,
  Bool,
  Number,
  Date,
}
